import { useState, useEffect } from 'react'
import { Table, Card, Button, Input, Select, Space, Modal, Form, message, Popconfirm, Tag } from 'antd'
import { PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { bomApi, productApi } from '../services/api'

interface BOMItem {
  id: number
  material_id: number
  material_type: number // 1=主料 2=辅料
  quantity: number
  unit: string
  loss_rate: number
  material?: {
    id: number
    name: string
    code: string
    unit: string
  }
}

interface BOM {
  id: number
  product_id: number
  name: string
  status: number
  remark: string
  product?: {
    id: number
    name: string
    code: string
    main_image: string
    unit: string
  }
  items?: BOMItem[]
}

const BOMPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<BOM[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<number | undefined>(undefined)

  const [modalVisible, setModalVisible] = useState(false)
  const [editingBOM, setEditingBOM] = useState<BOM | null>(null)
  const [form] = Form.useForm()
  const [products, setProducts] = useState<any[]>([])
  const [bomItems, setBomItems] = useState<BOMItem[]>([])

  useEffect(() => {
    fetchBOMs()
    fetchProducts()
  }, [page, pageSize, status])

  const fetchBOMs = async () => {
    setLoading(true)
    try {
      const res: any = await bomApi.getBOMs({ page, page_size: pageSize, keyword, status })
      setData(res.data.list || [])
      setTotal(res.data.total || 0)
    } catch (err) {
      message.error('获取BOM列表失败')
    }
    setLoading(false)
  }

  const fetchProducts = async () => {
    try {
      const res: any = await productApi.getProducts({ page: 1, page_size: 1000 })
      setProducts(res.data.list || [])
    } catch (err) {
      console.error('获取商品列表失败')
    }
  }

  const handleSearch = () => {
    setPage(1)
    fetchBOMs()
  }

  const handleAdd = () => {
    setEditingBOM(null)
    setBomItems([])
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = async (record: BOM) => {
    try {
      const res: any = await bomApi.getBOM(record.id)
      const bom = res.data
      setEditingBOM(bom)
      setBomItems((bom.items || []).map((item: any) => ({
        ...item,
        material_type: item.material_type || 1
      })))
      form.setFieldsValue({
        product_id: bom.product_id,
        name: bom.name,
        remark: bom.remark
      })
      setModalVisible(true)
    } catch (err) {
      message.error('获取BOM详情失败')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await bomApi.deleteBOM(id)
      message.success('删除成功')
      fetchBOMs()
    } catch (err: any) {
      message.error(err.response?.data?.message || '删除失败')
    }
  }

  const handleStatusChange = async (id: number, newStatus: number) => {
    try {
      await bomApi.updateStatus(id, newStatus)
      message.success(newStatus === 1 ? '已启用' : '已停用')
      fetchBOMs()
    } catch (err: any) {
      message.error(err.response?.data?.message || '操作失败')
    }
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      if (bomItems.length === 0) {
        message.error('请至少添加一个原材料')
        return
      }
      const postData = {
        ...values,
        items: bomItems.map(item => ({
          material_id: item.material_id,
          material_type: item.material_type || 1,
          quantity: 0,
          unit: ''
        }))
      }

      if (editingBOM) {
        await bomApi.updateBOM(editingBOM.id, postData)
        message.success('更新成功')
      } else {
        await bomApi.createBOM(postData)
        message.success('创建成功')
      }
      setModalVisible(false)
      fetchBOMs()
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || '保存失败'
      message.error(msg)
    }
  }

  const addBomItem = () => {
    setBomItems([...bomItems, { id: 0, material_id: 0, material_type: 1, quantity: 0, unit: '', loss_rate: 0 }])
  }

  const updateBomItem = (index: number, field: string, value: any) => {
    const newItems = [...bomItems]
    ;(newItems[index] as any)[field] = value
    setBomItems(newItems)
  }

  const removeBomItem = (index: number) => {
    setBomItems(bomItems.filter((_, i) => i !== index))
  }

  const columns = [
    {
      title: '成品',
      dataIndex: 'product',
      render: (product: any) => (
        <Space>
          {product?.main_image && (
            <img src={product.main_image} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
          )}
          <div>
            <div style={{ fontWeight: 500 }}>{product?.name}</div>
            <div style={{ color: '#999', fontSize: 12 }}>{product?.code}</div>
          </div>
        </Space>
      )
    },
    {
      title: '配方名称',
      dataIndex: 'name',
      render: (name: string) => name || '-'
    },
    {
      title: '原料',
      render: (_: any, record: BOM) => {
        const mains = record.items?.filter(i => i.material_type === 1).length || 0
        const auxs = record.items?.filter(i => i.material_type === 2).length || 0
        return mains || auxs ? `主料${mains} 辅料${auxs}` : (record.items?.length || 0)
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'default'}>
          {status === 1 ? '启用' : '停用'}
        </Tag>
      )
    },
    {
      title: '操作',
      render: (_: any, record: BOM) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleStatusChange(record.id, record.status === 1 ? 0 : 1)}
          >
            {record.status === 1 ? '停用' : '启用'}
          </Button>
          <Popconfirm title="确定删除该BOM配方吗？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <Card
      title="BOM配方管理"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          新增配方
        </Button>
      }
    >
      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="搜索成品名称/编码"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 200 }}
        />
        <Select
          placeholder="状态"
          value={status}
          onChange={val => setStatus(val)}
          allowClear
          style={{ width: 120 }}
          options={[
            { label: '启用', value: 1 },
            { label: '停用', value: 0 }
          ]}
        />
        <Button icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps) }
        }}
      />

      <Modal
        title={editingBOM ? '编辑BOM配方' : '新增BOM配方'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="product_id" label="成品" rules={[{ required: true, message: '请选择成品' }]}>
            <Select
              showSearch
              placeholder="选择成品"
              optionFilterProp="children"
              disabled={!!editingBOM}
            >
              {products.map(p => (
                <Select.Option key={p.id} value={p.id}>
                  {p.name} ({p.code})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="name" label="配方名称">
            <Input placeholder="如：标准配方" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>

        <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 500 }}>原材料明细</div>
        <Table
          size="small"
          dataSource={bomItems}
          rowKey={(_, index) => `item-${index}`}
          pagination={false}
          columns={[
            {
              title: '类型', width: 100,
              render: (_, __, index) => (
                <Select
                  value={bomItems[index].material_type || 1}
                  onChange={val => updateBomItem(index, 'material_type', val)}
                  style={{ width: '100%' }}
                >
                  <Select.Option value={1}>主料</Select.Option>
                  <Select.Option value={2}>辅料</Select.Option>
                </Select>
              )
            },
            {
              title: '原材料',
              render: (_, __, index) => (
                <Select
                  showSearch
                  style={{ width: '100%' }}
                  placeholder="选择原材料"
                  value={bomItems[index].material_id || undefined}
                  onChange={val => updateBomItem(index, 'material_id', val)}
                  optionFilterProp="children"
                >
                  {products.map(p => (
                    <Select.Option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </Select.Option>
                  ))}
                </Select>
              )
            },
            {
              title: '操作', width: 60,
              render: (_, __, index) => (
                <Button type="link" danger size="small" onClick={() => removeBomItem(index)}>删除</Button>
              )
            }
          ]}
        />
        <Button type="dashed" icon={<PlusOutlined />} onClick={addBomItem} style={{ width: '100%', marginTop: 8 }}>
          添加原材料
        </Button>
      </Modal>
    </Card>
  )
}

export default BOMPage
